// src/types/lineSubject.ts

export interface LineSubjectMapping {
  id: string;
  lineId: string;
  lineName: string;
  classCode: string;
  className: string;
  faculty: string;
  curriculumArea?: string;
  yearLevel?: string;
  semester?: 'semester1' | 'semester2' | 'fullyear';
  gridId?: string; // Optional grid reference for multi-grid support
  gridName?: string; // Optional grid name for display
}

export interface LineSubjectConfig {
  gridId: string;
  mappings: LineSubjectMapping[];
  lastUpdated: Date;
  importedFrom?: string; // CSV filename
}

export interface SubjectLineAvailability {
  [subjectCode: string]: {
    [lineId: string]: boolean; // true if subject runs on this line
  };
}

// Helper functions
export const createSubjectAvailabilityMap = (mappings: LineSubjectMapping[]): SubjectLineAvailability => {
  const map: SubjectLineAvailability = {};
  
  
  mappings.forEach(mapping => {
    // Store by classCode (e.g., "12HUMACCC")
    if (!map[mapping.classCode]) {
      map[mapping.classCode] = {};
    }
    map[mapping.classCode][mapping.lineId] = true;
    
    // ALSO store by className (e.g., "Accounting12") for dual-key lookup
    // This allows the grid to find availability using either key
    if (mapping.className && !map[mapping.className]) {
      map[mapping.className] = {};
    }
    if (mapping.className) {
      map[mapping.className][mapping.lineId] = true;
    }
    
    // Also create variations without spaces and special characters for fuzzy matching
    const normalizedClassName = mapping.className?.replace(/[\s\-_]/g, '').toLowerCase();
    if (normalizedClassName && !map[normalizedClassName]) {
      map[normalizedClassName] = {};
    }
    if (normalizedClassName) {
      map[normalizedClassName][mapping.lineId] = true;
    }
  });
  
  const totalKeys = Object.keys(map).length;
  
  return map;
};

export const isSubjectAvailableOnLine = (
  subjectCode: string, 
  lineId: string, 
  availability: SubjectLineAvailability
): boolean => {
  // Try exact match first
  let result = availability[subjectCode]?.[lineId] || false;
  
  // If no exact match, try with normalized subject code
  if (!result && subjectCode) {
    const normalized = subjectCode.replace(/[\s\-_]/g, '').toLowerCase();
    result = availability[normalized]?.[lineId] || false;
  }
  
  // CRITICAL FIX: If still no match, try with simplified line IDs
  // Grid uses "AS1", "BS1" but CSV data might use "A", "B"
  if (!result && lineId) {
    // Extract the base letter from compound line IDs (AS1 -> A, BS1 -> B, etc.)
    const baseLineId = lineId.charAt(0); // Gets first character (A from AS1, B from BS1)
    
    // Try exact subject code with base line ID
    result = availability[subjectCode]?.[baseLineId] || false;
    
    // Try normalized subject code with base line ID
    if (!result && subjectCode) {
      const normalized = subjectCode.replace(/[\s\-_]/g, '').toLowerCase();
      result = availability[normalized]?.[baseLineId] || false;
    }
  }
  
  
  return result;
};